import { motion } from "framer-motion";
import { Check, ArrowRight, ShieldCheck } from "lucide-react";
import { useSmoothScroll } from "../context/ScrollContext";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const plans = [
  {
    testid: "pricing-card-cash",
    tag: "Pay per visit",
    name: "Single Session Consultation",
    price: "$200",
    period: "per appointment",
    features: [
      "90-minute deep dive session",
      "Custom birth plan or postpartum strategy",
      "Virtual or in-person in San Joaquin County",
      "Direct access resources guide",
    ],
    dark: false,
  },
  {
    testid: "pricing-card-package",
    tag: "Most chosen",
    name: "Complete Birth Doula Package",
    price: "Custom",
    period: "full-spectrum support",
    features: [
      "2 prenatal visits + 24/7 on-call labor support",
      "Continuous birth attendance",
      "2 postpartum home visits",
      "HSA/FSA and insurance invoice assistance",
    ],
    dark: true,
  },
];

const PricingSection = () => {
  const { scrollTo } = useSmoothScroll();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 lg:py-28 bg-[#F4F1FA] border-y border-[#E6E0F0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          variants={reveal}
          custom={0}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="max-w-2xl mb-16"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-heading font-bold text-[#F2644C] mb-4">
            Pricing
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#2B1B45] leading-tight">
            Transparent,{" "}
            <span className="font-serif italic font-semibold text-[#6D28D9]">
              accessible
            </span>{" "}
            investment
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5B5470] leading-relaxed">
            Every family deserves to know what care costs before it begins.
            Your first intro consultation is always free.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
          {plans.map((plan, i) => (
            <motion.article
              key={plan.testid}
              data-testid={plan.testid}
              variants={reveal}
              custom={i + 1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
              className={`rounded-2xl p-8 sm:p-10 transition-shadow duration-500 hover:shadow-xl ${
                plan.dark
                  ? "bg-[#2E1B4D] border border-white/10 text-white"
                  : "bg-white border border-[#E6E0F0] shadow-sm text-[#2B1B45]"
              }`}
            >
              <span
                className={`inline-block text-[10px] font-heading font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full mb-6 ${
                  plan.dark ? "bg-[#F2644C] text-white" : "bg-[#2563EB]/10 text-[#2563EB]"
                }`}
              >
                {plan.tag}
              </span>
              <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-4">
                {plan.name}
              </h3>
              <p className="flex items-baseline gap-2 mb-1">
                <span
                  className={`font-heading text-5xl font-extrabold tracking-tight ${
                    plan.dark ? "text-white" : "text-[#2B1B45]"
                  }`}
                >
                  {plan.price}
                </span>
              </p>
              <p
                className={`text-sm mb-7 ${
                  plan.dark ? "text-[#CFC7E6]" : "text-[#7A7390]"
                }`}
              >
                {plan.period}
              </p>
              <ul className="space-y-3">
                {plan.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-2.5 text-sm ${
                      plan.dark ? "text-[#CFC7E6]" : "text-[#5B5470]"
                    }`}
                  >
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#F2644C]" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        <motion.div
          variants={reveal}
          custom={3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-8 max-w-5xl rounded-2xl border border-[#E6E0F0] bg-white px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <ShieldCheck className="h-6 w-6 text-[#2563EB] shrink-0" />
          <p className="text-sm sm:text-base text-[#5B5470] leading-relaxed">
            <span className="font-semibold text-[#2B1B45]">
              Some insurance plans may be accepted,
            </span>{" "}
            including Medi-Cal and FSA/HSA reimbursement options. Bring your
            plan details to your free consultation and we will check together.
          </p>
        </motion.div>

        <motion.button
          data-testid="pricing-cta-button"
          variants={reveal}
          custom={4}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          onClick={() => scrollTo("#contact")}
          className="mt-10 inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-[#F2644C] text-white text-sm font-bold tracking-wide hover:bg-[#D9523B] transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 group"
        >
          Ask about pricing
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>
      </div>
    </section>
  );
};

export default PricingSection;
